import { useEffect, useState } from 'react';
import { LAUNCH_DEADLINE, LAUNCH_WINDOW_HOURS } from '../config/launch';

type CountdownState = {
  days: string;
  hours: string;
  minutes: string;
  seconds: string;
  expired: boolean;
};

const STORAGE_KEY = 'screen-adapt-launch-deadline';
const WINDOW_MS = LAUNCH_WINDOW_HOURS * 60 * 60 * 1000;
const TWO_DAYS_IN_SECONDS = 172800;

const pad = (value: number): string => String(Math.max(0, value)).padStart(2, '0');

/**
 * Each visitor gets LAUNCH_WINDOW_HOURS from their first visit, never running
 * past the hard launch deadline.
 */
function getDeadline(): number {
  const hardDeadline = new Date(LAUNCH_DEADLINE).getTime();

  if (typeof window === 'undefined') {
    return Date.now() + WINDOW_MS;
  }

  let rolling = Number(window.localStorage.getItem(STORAGE_KEY));
  if (!Number.isFinite(rolling) || rolling <= Date.now()) {
    rolling = Date.now() + WINDOW_MS;
    try {
      window.localStorage.setItem(STORAGE_KEY, String(rolling));
    } catch {
      /* storage unavailable — the window just restarts on reload */
    }
  }

  return Number.isFinite(hardDeadline) ? Math.min(rolling, hardDeadline) : rolling;
}

function compute(deadline: number): CountdownState {
  const remaining = deadline - Date.now();
  if (!Number.isFinite(deadline) || remaining <= 0) {
    return { days: '00', hours: '00', minutes: '00', seconds: '00', expired: true };
  }

  const totalSeconds = Math.floor(remaining / 1000);
  const showDays = totalSeconds >= TWO_DAYS_IN_SECONDS;

  return {
    days: showDays ? pad(Math.floor(totalSeconds / 86400)) : '00',
    hours: pad(
      showDays ? Math.floor((totalSeconds % 86400) / 3600) : Math.floor(totalSeconds / 3600),
    ),
    minutes: pad(Math.floor((totalSeconds % 3600) / 60)),
    seconds: pad(totalSeconds % 60),
    expired: false,
  };
}

export function useCountdown(): CountdownState {
  const [deadline] = useState<number>(() => getDeadline());
  const [state, setState] = useState<CountdownState>(() => compute(deadline));

  useEffect(() => {
    const id = window.setInterval(() => setState(compute(deadline)), 1000);
    return () => window.clearInterval(id);
  }, [deadline]);

  return state;
}
